import { useState } from 'react'
import { Link, Navigate } from 'react-router-dom'
import { AnimatePresence, motion, useReducedMotion, type Variants } from 'framer-motion'
import { ArrowLeft, Mail, ShieldCheck } from 'lucide-react'
import { AuroraBackdrop } from '../components/AuroraBackdrop'
import { EmailSignIn } from '../components/EmailSignIn'
import { GoogleSignIn, type NoWorkspace } from '../components/GoogleSignIn'
import { LogoOrbit } from '../components/LogoOrbit'
import { useAuth } from '../lib/auth'

const EASE = [0.16, 1, 0.3, 1] as const

function hasEmailLinkFragment(): boolean {
  if (typeof window === 'undefined' || !window.location.hash) return false
  const params = new URLSearchParams(window.location.hash.slice(1))
  return Boolean(params.get('challenge') && params.get('token'))
}

/**
 * The front door of app.afos360.ai: a full-screen sign-in with the AFOS 360 mark, an
 * animated aurora, and a glass card. Google is the primary method when configured; email
 * codes sit behind "Use email code instead" (or are the only method when Google is not
 * configured). Signed-in users go straight to the dashboard.
 */
export function SignInPage() {
  const { user, loading, googleClientId, emailSignIn, authPosture, signInDemo } = useAuth()
  const reduce = useReducedMotion()
  // A one-time link from a sign-in mail opens the email form straight away, so its
  // address confirmation shows (EmailSignIn still requires the person to confirm first).
  const [showEmail, setShowEmail] = useState(hasEmailLinkFragment)
  const [noWorkspace, setNoWorkspace] = useState<NoWorkspace | null>(null)
  const [demoError, setDemoError] = useState<string | null>(null)

  if (!loading && user) return <Navigate to="/app" replace />

  const hasGoogle = Boolean(googleClientId)
  const emailVisible = emailSignIn && (!hasGoogle || showEmail)
  const demoAllowed = Boolean(authPosture?.demo_allowed)
  const noMethod = !loading && !hasGoogle && !emailSignIn && !demoAllowed

  const stagger: Variants = {
    hidden: {},
    show: { transition: { staggerChildren: reduce ? 0 : 0.12, delayChildren: reduce ? 0 : 0.35 } },
  }
  const rise: Variants = {
    hidden: reduce ? { opacity: 0 } : { opacity: 0, y: 18, filter: 'blur(6px)' },
    show: { opacity: 1, y: 0, filter: 'blur(0px)', transition: { duration: 0.7, ease: EASE } },
  }

  return (
    <div className="relative flex min-h-[100svh] flex-col overflow-hidden text-[var(--color-foam)]">
      <AuroraBackdrop />

      <main className="relative z-10 flex flex-1 flex-col items-center justify-center px-4 py-10 sm:px-6">
        <motion.div variants={stagger} initial="hidden" animate="show" className="flex w-full max-w-md flex-col items-center">
          <motion.div variants={rise}>
            <LogoOrbit />
          </motion.div>

          <motion.div variants={rise} className="-mt-2 text-center">
            <p className="font-[family-name:var(--font-display)] text-4xl font-extrabold tracking-tight sm:text-5xl">
              AFOS <span className="text-gradient">360</span>
            </p>
            <p className="mt-2 text-[11px] uppercase tracking-[0.32em] text-[var(--color-mist)]">App Factory Operating System</p>
          </motion.div>

          <motion.section
            variants={rise}
            aria-labelledby="signin-title"
            className="glass neon-ring relative mt-8 w-full overflow-hidden rounded-[2rem] p-6 shadow-[0_30px_80px_-20px_rgba(0,0,0,0.65)] sm:p-8"
          >
            <div
              aria-hidden
              className="pointer-events-none absolute inset-x-8 top-0 h-px bg-gradient-to-r from-transparent via-[var(--color-cyan)] to-transparent opacity-70"
            />
            <AnimatePresence mode="wait" initial={false}>
              {noWorkspace ? (
                <motion.div
                  key="no-workspace"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.35, ease: EASE }}
                  className="space-y-5 text-center"
                >
                  <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-[rgba(255,179,71,0.12)] text-[var(--color-amber)]">
                    <ShieldCheck size={22} aria-hidden />
                  </div>
                  <div>
                    <h1 id="signin-title" className="font-[family-name:var(--font-display)] text-2xl font-bold">
                      No workspace yet
                    </h1>
                    <p className="mt-2 text-sm text-[var(--color-mist)]">
                      {noWorkspace.email ? (
                        <>
                          <span className="font-semibold text-[var(--color-foam)]">{noWorkspace.email}</span> is verified,
                          but it isn't a member of any AFOS 360 workspace.
                        </>
                      ) : (
                        'Your Google account is verified, but it is not a member of any AFOS 360 workspace.'
                      )}{' '}
                      Ask your workspace admin for an invite, or request access.
                    </p>
                  </div>
                  <Link
                    to="/signup?src=no_workspace"
                    className="block w-full rounded-full bg-[var(--color-cyan)] px-5 py-3 text-center font-semibold text-[var(--color-void)] transition hover:brightness-110"
                  >
                    Request access
                  </Link>
                  <button
                    type="button"
                    onClick={() => setNoWorkspace(null)}
                    className="inline-flex items-center gap-1.5 text-sm text-[var(--color-cyan)] hover:underline"
                  >
                    <ArrowLeft size={14} aria-hidden /> Try a different account
                  </button>
                </motion.div>
              ) : (
                <motion.div
                  key="methods"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.35, ease: EASE }}
                >
                  <h1 id="signin-title" className="text-center font-[family-name:var(--font-display)] text-2xl font-bold tracking-tight sm:text-3xl">
                    Sign in to your factory
                  </h1>
                  <p className="mt-2 text-center text-sm text-[var(--color-mist)]">
                    {hasGoogle
                      ? 'Use the Google account your workspace invited.'
                      : 'Invite only. Members and invited guests get a one-time sign-in code by email.'}
                  </p>

                  <div className="mt-7 space-y-4">
                    {loading ? (
                      <div className="h-11 animate-pulse rounded-full bg-white/10" aria-hidden />
                    ) : (
                      <>
                        <GoogleSignIn onNoWorkspace={setNoWorkspace} />

                        {hasGoogle && emailSignIn && (
                          <>
                            <div className="flex items-center gap-3 text-[11px] uppercase tracking-[0.2em] text-[var(--color-mist)]">
                              <span className="h-px flex-1 bg-[var(--color-line)]" />
                              or
                              <span className="h-px flex-1 bg-[var(--color-line)]" />
                            </div>
                            {!showEmail && (
                              <button
                                type="button"
                                onClick={() => setShowEmail(true)}
                                className="flex w-full items-center justify-center gap-2 rounded-full border border-[var(--color-line)] bg-white/[0.03] px-5 py-3 text-sm font-semibold text-[var(--color-foam)] transition hover:border-[var(--color-cyan)] hover:bg-white/[0.06]"
                              >
                                <Mail size={16} aria-hidden /> Use email code instead
                              </button>
                            )}
                          </>
                        )}

                        <AnimatePresence initial={false}>
                          {emailVisible && (
                            <motion.div
                              key="email"
                              initial={hasGoogle ? { opacity: 0, height: 0 } : false}
                              animate={{ opacity: 1, height: 'auto' }}
                              exit={{ opacity: 0, height: 0 }}
                              transition={{ duration: 0.35, ease: EASE }}
                              className="overflow-hidden"
                            >
                              <EmailSignIn />
                            </motion.div>
                          )}
                        </AnimatePresence>

                        {demoAllowed && (
                          <button
                            type="button"
                            onClick={() => {
                              setDemoError(null)
                              void signInDemo().catch(() => setDemoError('Demo sign-in failed'))
                            }}
                            className="w-full text-center text-xs text-[var(--color-mist)] hover:text-[var(--color-foam)] hover:underline"
                          >
                            Local demo sign-in
                          </button>
                        )}
                        {demoError && (
                          <p role="alert" className="text-center text-sm text-[var(--color-rose)]">
                            {demoError}
                          </p>
                        )}
                        {noMethod && (
                          <p role="alert" className="text-center text-sm text-[var(--color-amber)]">
                            Sign-in is temporarily unavailable. Please try again shortly.
                          </p>
                        )}
                      </>
                    )}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.section>

          <motion.p variants={rise} className="mt-6 text-center text-sm text-[var(--color-mist)]">
            New to AFOS 360?{' '}
            <Link to="/signup" className="text-[var(--color-cyan)] hover:underline">
              Request early access
            </Link>
          </motion.p>
        </motion.div>
      </main>

      <footer className="relative z-10 pb-6 text-center text-[11px] text-[var(--color-mist)]/70">
        Access is by invitation. © AFOS 360
      </footer>
    </div>
  )
}
